import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import useLoggedStatus from "../../hooks/useLoggedStatus";
import { translation } from "../../translation";
import { logOut } from "../../redux/appSlices/auth/authSlice";
import {
  usePatchCollaboratorMutation,
  usePatchProfileMutation,
} from "../../redux/appSlices/collaborator/collaboratorApiSlice";
import { getProfilePicturePatchTarget } from "../../utils/profilePicture";
import { useGenerateWalletPassMutation, useGenerateGoogleWalletPassMutation } from "../../redux/appSlices/generalApiSlice";
import { addCollaboratorToWallet } from "../../utils/wallet";
import useTracking from "../../hooks/useTracking";
import EditComponants from "./EditComponants";
import MeetingsModal from "./MeetingsModal";
import { toast } from "react-toastify";

function MainCard({ infinite_animation, activePublicProfile: activePublicProfileProp, onLoginModalChange }) {
  const logged = useLoggedStatus();
  const dispatch = useDispatch();

  const collaborator = useSelector(
    (state) => state.collaborator.collaboratorInfo,
  );
  const lang = useSelector(
    (state) => state.collaborator.collaboratorInfo.language,
  );
  const restriction = collaborator?.backoffice_id;

  const [openLoginPopup, setOpenLoginPopup] = useState(false);
  const [openEditPopup, setOpenEditPopup] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [imageSrcTest, setImageSrcTest] = useState(null);
  const cropperRef = useState(null);
  const [imageElement, setImageElement] = useState();
  const [swipped, setSwipped] = useState(false);
  const [clickCounter, setClickCounter] = useState(0);
  const [profileLoading, setProfileLoading] = useState(false);
  const [componantToEditId, setComponantToEditId] = useState(0);
  const [fetchWalletPass, setFetchWalletPass] = useState(false);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [settingsTab, setSettingsTab] = useState("settings");

  useEffect(() => {
    onLoginModalChange?.(openLoginPopup);
  }, [openLoginPopup, onLoginModalChange]);

  // Use the active public profile from props (passed from parent Profile.js)
  const activePublicProfile = activePublicProfileProp;

  const [patchCollaborator] = usePatchCollaboratorMutation();
  const [patchProfile] = usePatchProfileMutation();
  const [generateWalletPass] = useGenerateWalletPassMutation();
  const [generateGoogleWalletPass] = useGenerateGoogleWalletPassMutation();
  const { sendTracking } = useTracking();

  const handleFileUpload = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setImageSrcTest(file);
      setComponantToEditId(11);
      setOpenEditPopup(true);
      setProfileLoading(true);
      e.target.value = "";
    }
  };

  const [startX, setX] = useState();
  const [startY, setY] = useState();
  const [startMouseX, setMouseX] = useState();
  const [mouseDown, setMouseDown] = useState(false);

  const handleSwipeOrClick = () => {
    setClickCounter((prevCounter) => prevCounter + 1);
  };

  const detectSwipeX = (endX) => {
    if (endX < startX) {
      setSwipped(true);
      handleSwipeOrClick();
    }
    setTimeout(() => {
      setSwipped(false);
    }, 300);
  };

  const swiperCard = () => {
    setSwipped(true);
    setTimeout(() => {
      setSwipped(false);
    }, 300);
  };

  useEffect(() => {
    if (infinite_animation) {
      setTimeout(() => {
        swiperCard();
      }, 400);
      setInterval(() => {
        swiperCard();
      }, 5000);
    }
  }, []);

  const detectSwipeMouseX = (endX) => {
    if (endX < startMouseX) {
      setSwipped(true);
      handleSwipeOrClick();
    }
    setTimeout(() => {
      setSwipped(false);
    }, 300);
  };

  const className = ["translate-x-[-300px]", "translate-x-[0px]"];
  const actionButtonClass =
    "flex w-full min-w-0 items-center justify-start gap-2 py-2 px-1 rounded-lg transition-colors duration-200 group hover:bg-gray-50";
  const actionButtonLabelClass =
    "text-left text-[15px] font-medium leading-tight text-gray-700 whitespace-nowrap group-hover:text-gray-900";
  const actionButtonLabelDangerClass =
    "text-left text-[15px] font-medium leading-tight text-red-500 whitespace-nowrap group-hover:text-red-600";
  const imageBasePath = (process.env.REACT_APP_IMAGE_PATH || "")
    .toString()
    .trim()
    .replace(/^['"]+|['"]+$/g, "");
  const cleanProfilePicture = (collaborator?.profile_picture || "")
    .toString()
    .trim()
    .replace(/^['"]+|['"]+$/g, "");

  const handleAddToWallet = async () => {
    setFetchWalletPass(true);

    try {
      const walletType = await addCollaboratorToWallet({
        collaboratorId: collaborator._id,
        generateAppleWalletPass: generateWalletPass,
        generateGoogleWalletPass,
      });

      sendTracking({
        action: 66,
        collaborator_id: collaborator.collaborator_id,
        componant:
          walletType === "google"
            ? "GoogleWalletButtonMainCard"
            : "AppleWalletButtonMainCard",
      });
    } catch (error) {
      console.error("Error generating wallet pass:", error);
      toast.error(
        lang === "ar"
          ? "خطأ في إضافة إلى المحفظة"
          : lang === "en"
            ? "Error adding to wallet"
            : "Erreur lors de l'ajout au portefeuille",
      );
    } finally {
      setTimeout(() => {
        setFetchWalletPass(false);
      }, 2000);
    }
  };

  const handleOpenSettings = () => {
    setSettingsTab("settings");
    setOpenEditPopup(true);
    setComponantToEditId(10);
  };

  const handleUpdateContact = () => {
    setOpenEditPopup(true);
    setComponantToEditId(9);
  };

  const handleViewContacts = () => {
    setShowContactsModal(true);
  };

  const handleDeleteProfileImage = async () => {
    setImageSrc(null);
    setShowDeleteConfirmation(false);

    const patchTarget = getProfilePicturePatchTarget(collaborator);
    if (!patchTarget) return;

    try {
      if (patchTarget.type === "profile") {
        await patchProfile({
          id: patchTarget.id,
          content: { profile_picture: "" },
        }).unwrap();
      } else {
        await patchCollaborator({
          id: patchTarget.id,
          content: { profile_picture: "" },
        }).unwrap();
      }
    } catch (err) {
      toast.error(err?.data?.message?.[lang]);
    }
  };

  const handleLogout = () => {
    dispatch(logOut());
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key !== "user_token") {
        localStorage.removeItem(key);
      }
    });
    localStorage.setItem("logout", "true");
    window.location.reload();
  };

  const displayProfile =
    !logged && activePublicProfile ? activePublicProfile : collaborator;
  const displayFirstName =
    displayProfile?.first_name || collaborator?.first_name;
  const displayLastName = displayProfile?.last_name || collaborator?.last_name;
  const displayTitle = displayProfile?.title || collaborator?.title;
  const displayCompany = displayProfile?.company || collaborator?.company;
  const displayProfilePicture =
    displayProfile?.profile_picture || collaborator?.profile_picture;
  return (
    <>
      <section
        className={`z-10 ${
          logged
            ? ""
            : `transform ${swipped ? "translate-x-[-300px]" : "translate-x-[0px]"} transition-transform ease-in duration-[.3s]`
        }`}
      >
        <div
          className={` bg-white rounded-[10px] mt-[-115px] shadow-xl ${logged ? "px-3 py-5 min-h-0" : "p-0 h-[162px] overflow-hidden"} flex flex-col`}
          onTouchStart={
            logged
              ? undefined
              : (e) => {
                  setX(e.targetTouches[0].pageX);
                }
          }
          onTouchEnd={
            logged
              ? undefined
              : (e) => {
                  detectSwipeX(e.changedTouches[0].pageX);
                }
          }
          onMouseDown={
            logged
              ? undefined
              : (e) => {
                  setMouseX(e.clientX);
                  setMouseDown(true);
                }
          }
          onMouseUp={
            logged
              ? undefined
              : (e) => {
                  if (mouseDown) {
                    detectSwipeMouseX(e.clientX);
                  }
                  setMouseDown(false);
                }
          }
        >
          {/* Profile Section - Image on Left, Info on Right */}
          <div
            className={`flex gap-3 ${logged ? "items-start border-b border-gray-100 pb-4" : "h-full items-center"}`}
          >
            {/* Profile Image */}
            <div
              className={`relative shrink-0 ${logged ? "w-[88px]" : "w-[134px]"}`}
            >
              <div className="flex flex-col items-center gap-1.5">
                <div className="relative">
                  {logged ? (
                    <label
                      htmlFor="profilePicture"
                      className="relative block cursor-pointer group"
                      aria-label={
                        translation.mainCard.editProfilePictureAria[lang]
                      }
                    >
                      {displayProfilePicture ? (
                        <div className="h-[88px] w-[88px] rounded-full overflow-hidden border-2 border-gray-100 shadow-md transition-opacity group-hover:opacity-90">
                          <img
                            className="object-cover h-full w-full"
                            src={`${imageBasePath}${displayProfilePicture}`}
                            alt="profile picture"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-[88px] w-[88px] rounded-full bg-gradient-to-br from-gray-100 to-gray-200 border-2 border-gray-100 shadow-md transition-opacity group-hover:opacity-90">
                          <img
                            className="w-10 h-10"
                            src="/assets/img/icons/default-profile.svg"
                            alt="profile picture"
                          />
                        </div>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/0 group-hover:bg-black/20 transition-colors">
                        <svg
                          className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      </div>
                    </label>
                  ) : displayProfilePicture ? (
                    <div className="h-[162px] w-[134px]">
                      <img
                        className={`object-cover h-[162px] w-[134px] ${lang === "ar" ? "rounded-r-xl" : "rounded-l-xl"}`}
                        src={`${imageBasePath}${displayProfilePicture}`}
                        alt="profile picture"
                      />
                    </div>
                  ) : (
                    <div className="h-[162px] w-[134px] flex items-center justify-center">
                      <img
                        className="object-cover h-[130px]"
                        src="/assets/img/icons/default-profile.svg"
                        alt="profile picture"
                      />
                    </div>
                  )}

                  {/* Delete Icon Button - Top Right, Above Border */}
                  {logged && collaborator.profile_picture && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowDeleteConfirmation(true);
                      }}
                      className="absolute -top-0.5 -right-0.5 w-6 h-6 bg-white border border-gray-300 hover:bg-red-50 hover:border-red-400 rounded-full flex items-center justify-center transition-colors shadow-sm z-10"
                    >
                      <svg
                        className="w-3 h-3 text-gray-600 hover:text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Modifier Button Below Image - Opens File Chooser Directly */}
                {logged && (
                  <label
                    htmlFor="profilePicture"
                    className="flex w-[88px] cursor-pointer items-center justify-center rounded-full border border-gray-300 bg-transparent px-2 py-1 text-center text-[10px] font-medium leading-tight text-gray-700 transition-colors duration-200 hover:border-gray-400 hover:bg-gray-50"
                  >
                    {translation.mainCard.updateProfilePicture[lang]}
                  </label>
                )}
                {/* Hidden File Input */}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  name="profile-image"
                  onChange={handleFileUpload}
                  id="profilePicture"
                />
              </div>

              {profileLoading && (
                <div className="absolute top-0 left-0 flex justify-center items-center h-[88px] w-[88px] rounded-full bg-white bg-opacity-90 z-30">
                  <img
                    src="/assets/img/loaders/loading.gif"
                    alt="loading"
                    className="w-8 h-8"
                  />
                </div>
              )}
            </div>

            {/* User Info */}
            <div
              className={`min-w-0 flex-1 ${logged ? "pt-0.5" : "py-3 pr-4"}`}
            >
              {swipped !== null && !logged && clickCounter % 2 !== 0 ? (
                <>
                  <h1 className="font-SteradianMedium font-bold text-[22px] tracking-[-.97px] text-gray-800">
                    {collaborator?.first_name},
                  </h1>
                  <p className="mt-2 tracking-[-.65px] text-gray-600">
                    {translation?.mainCard?.title?.[lang]}
                  </p>
                  <div
                    className={`flex text-[15px] items-center mt-3 cursor-pointer text-blue-600 hover:text-blue-700 ${lang === "ar" ? "font-lalezarRegular" : "font-SteradianMedium tracking-[1.41px]"}`}
                    onClick={() => {
                      setOpenLoginPopup(true);
                    }}
                  >
                    {translation?.loginPage?.loginTitle?.[lang]}{" "}
                    {lang === "ar" ? "←" : "→"}
                  </div>
                </>
              ) : (
                <>
                  <h1 className="font-SteradianMedium text-[22px] leading-tight tracking-[-.97px] text-gray-800 font-bold break-words">
                    {displayFirstName} {displayLastName}
                  </h1>
                  <div className="mt-1.5 text-[15px] leading-snug tracking-[-.65px] text-gray-600">
                    <p className="break-words">
                      {displayTitle?.[lang]}
                    </p>
                    <p className="mt-1 font-medium text-gray-700">
                      {restriction?.restrict_company_name
                        ? restriction.company_name?.[lang]
                        : displayCompany?.[lang]}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Action Buttons Section - Only show for logged-in users */}
          {logged && (
            <div className="mt-5 flex min-w-0 flex-col gap-0.5 overflow-x-auto">
              {/* Settings Button */}
              <button
                onClick={handleOpenSettings}
                className={actionButtonClass}
              >
                <svg
                  className="size-5 shrink-0 text-gray-500 group-hover:text-gray-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span className={actionButtonLabelClass}>
                  {lang === "ar"
                    ? "الوصول إلى الإعدادات"
                    : lang === "en"
                      ? "Access Settings"
                      : "Accéder aux Paramètres"}
                </span>
              </button>

              {/* Switch Profile Button - Only show if multi-profile is enabled */}
              {collaborator?.isMultiProfile && (
                <button
                  onClick={() => {
                    setSettingsTab("account");
                    setComponantToEditId(10);
                    setOpenEditPopup(true);
                  }}
                  className={actionButtonClass}
                >
                  {/* Two Users Icon */}
                  <svg
                    className="size-5 shrink-0 text-gray-500 group-hover:text-gray-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                  <span className={actionButtonLabelClass}>
                    {translation.settings.switchProfile.switchProfile[lang]}
                  </span>
                </button>
              )}

              {/* Update Contact Button */}
              <button
                onClick={handleUpdateContact}
                className={actionButtonClass}
              >
                <svg
                  className="size-5 shrink-0 text-gray-500 group-hover:text-gray-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                <span className={actionButtonLabelClass}>
                  {lang === "ar"
                    ? "تحديث بطاقة الاتصال الخاصة بي"
                    : lang === "en"
                      ? "Update my contact card"
                      : "Mettre à jour ma fiche de contact"}
                </span>
              </button>

              {/* Contacts List Button */}
              <button
                onClick={handleViewContacts}
                className={actionButtonClass}
              >
                <svg
                  className="size-5 shrink-0 text-gray-500 group-hover:text-gray-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <span className={actionButtonLabelClass}>
                  {lang === "ar"
                    ? "عرض قائمة جهات الاتصال المحددة"
                    : lang === "en"
                      ? "View my identified contacts list"
                      : "Voir la liste de mes contacts identifiés"}
                </span>
              </button>

              {collaborator?.popcardEcard && (
                <button
                  onClick={handleAddToWallet}
                  disabled={fetchWalletPass}
                  className={`${actionButtonClass} disabled:opacity-50`}
                >
                  {fetchWalletPass ? (
                    <>
                      <img
                        src="/assets/img/loaders/loading.gif"
                        alt="loading"
                        className="h-5 w-5 shrink-0"
                      />
                      <span className={actionButtonLabelClass}>
                        {translation.mainButton.walletLoading[lang]}
                      </span>
                    </>
                  ) : (
                    <>
                      <svg
                        className="h-5 w-5 shrink-0 text-gray-500 group-hover:text-gray-700"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                        />
                      </svg>
                      <span className={actionButtonLabelClass}>
                        {translation.mainButton.addToWallet[lang]}
                      </span>
                    </>
                  )}
                </button>
              )}

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="group flex w-full min-w-0 items-center gap-2 rounded-lg px-1 py-2 transition-colors duration-200 hover:bg-red-50"
              >
                <svg
                  className="h-5 w-5 shrink-0 text-red-500 group-hover:text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                <span className={actionButtonLabelDangerClass}>
                  {lang === "ar"
                    ? "تسجيل الخروج"
                    : lang === "en"
                      ? "Logout"
                      : "Se déconnecter"}
                </span>
              </button>
            </div>
          )}
        </div>
      </section>

      {openLoginPopup && (
        <EditComponants componantId={12} setOpenEditPopup={setOpenLoginPopup} />
      )}
      {openEditPopup && (
        <EditComponants
          componantId={componantToEditId === 0 ? 11 : componantToEditId}
          setProfileLoading={setProfileLoading}
          setOpenEditPopup={setOpenEditPopup}
          imageSrc={imageSrcTest}
          settingsTab={settingsTab}
        />
      )}
      {showContactsModal && (
        <MeetingsModal
          show={showContactsModal}
          onClose={() => setShowContactsModal(false)}
          lang={lang}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black/50 z-[99999999] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            {/* Title */}
            <h3
              className={`text-[17px] font-medium text-gray-800 mb-3 ${lang === "ar" ? "!font-readexRegular" : "font-SteradianRegular"}`}
            >
              {lang === "ar"
                ? "حذف صورة الملف الشخصي"
                : lang === "en"
                  ? "Delete Profile Picture"
                  : "Supprimer la photo de profil"}
            </h3>

            {/* Message */}
            <p
              className={`text-[15px] text-gray-600 mb-6 ${lang === "ar" ? "!font-readexRegular" : "font-SteradianRegular"}`}
            >
              {lang === "ar"
                ? "هل أنت متأكد أنك تريد حذف صورة ملفك الشخصي؟"
                : lang === "en"
                  ? "Are you sure you want to delete your profile picture?"
                  : "Êtes-vous sûr de vouloir supprimer votre photo de profil ?"}
            </p>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirmation(false)}
                className={`flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors ${lang === "ar" ? "!font-readexRegular" : "font-SteradianRegular"}`}
              >
                {lang === "ar" ? "إلغاء" : lang === "en" ? "Cancel" : "Annuler"}
              </button>
              <button
                onClick={handleDeleteProfileImage}
                className={`flex-1 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors ${lang === "ar" ? "!font-readexRegular" : "font-SteradianRegular"}`}
              >
                {lang === "ar" ? "حذف" : lang === "en" ? "Delete" : "Supprimer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default MainCard;
